import React, { useState, useEffect, useContext } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import classNames from 'classnames';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import {
  getSchema,
  triggerNewRelic,
  triggerNewRelicTimeLog,
  shouldSuppressBanner
} from '../../utils/helpers';
import useFetch from '../../hooks/useFetch';
import { buildRevJetBanner } from '../../utils/revjet';
import { newRelicConstants } from '../../constants';
import {
  bannerTypes,
  SUPPRESSED_NVALUES
} from '../../constants/constants';
import styles from './SponsoredSkyscraperBanner.module.scss';

const { string, bool, shape } = proptypes;

const SponsoredSkyscraperBannerComponent = (props) => {
  const [hasData, setHasData] = useState(false);
  const experienceContext = useContext(ExperienceContext);
  const {
    browserId, pageContext, slugId, renderHarmony
  } = props;
  let validBrowserId = browserId || experienceContext?.cookie?.adobeCookie?.MCMID;
  let { mcvisID, guestUserID } = useThdCustomer();
  const suppressedNValues = useConfigService(SUPPRESSED_NVALUES);
  if (renderHarmony) {
    validBrowserId = '1234';
    guestUserID = '4321';
  }

  let { schema } = props;
  const cx = classNames.bind(styles);

  schema = getSchema({ schema, pageContext });
  const schemaId = schema + '__skyscraper';

  const requestConfig = {
    mcvisID,
    guestUserID,
    browserId: validBrowserId,
    schema,
    schemaId,
    experienceContext,
    pageContext,
    setHasData,
    bannerType: bannerTypes.SKYSCRAPER,
    slugId,
    ignoreStoreId: true
  };

  const [isBranded, _] = useState(shouldSuppressBanner({ experienceContext, pageContext, suppressedNValues }));

  const {
    data: bannerData, status, apiRequestTimestamp, apiResponseTimestamp, adServer: bannerApiAdServer
  } = useFetch(requestConfig);

  useEffect(() => {
    const adType = newRelicConstants.SKYSCRAPER_BANNER;
    if (status === 200) {
      requestConfig.startTime = apiRequestTimestamp;
      setHasData(true);
      buildRevJetBanner({ data: bannerData, requestConfig, adType });
    }
    if (status) {
      triggerNewRelic(adType, `BANNER-API-${status}`);
      // SEND TIMESTAMPS TO CALCULATE API RESPONSE TIME
      triggerNewRelicTimeLog(newRelicConstants.SKYSCRAPER_BANNER_API_RESPONSE, apiRequestTimestamp, apiResponseTimestamp, bannerApiAdServer);
      // SEND TIME FROM BANNER INITIATED TO BANNER WINNER RECEIVED
      triggerNewRelicTimeLog(newRelicConstants.SKYSCRAPER_BANNER_AD_INIT_TO_WINNER, apiRequestTimestamp, apiResponseTimestamp, bannerApiAdServer);
    }
  }, [status]);

  useEffect(() => {
    initImpressionWatchers();
  }, [hasData]);

  if (!hasData || isBranded || schema === 'event_browse_sponsored') {
    return null;
  }

  return (
    <div id="sponsored-skyscraper-banner" className={cx('sponsored_skyscraper_banner')} data-component="SponsoredSkyscraperBanner">
      <div id={schemaId} data-testid="sponsored-skyscraper-banner" />
    </div>
  );
};

SponsoredSkyscraperBannerComponent.propTypes = {
  browserId: string,
  schema: string,
  slugId: string,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool,
  }),
  renderHarmony: bool
};

SponsoredSkyscraperBannerComponent.defaultProps = {
  browserId: '',
  schema: '',
  slugId: '',
  pageContext: {},
  renderHarmony: false
};

const SponsoredSkyscraperBanner = withDynamicComponent(
  withHydrator(
    {
      id: 'sponsored-skyscraper-banner',
      preserveCtxVal: 'clientStore'
    },
    SponsoredSkyscraperBannerComponent
  )
);

export { SponsoredSkyscraperBanner };
